import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake, Briefcase, Wallet, LineChart, BadgeCheck, Globe2, ArrowRight,
} from "lucide-react";
import PartnerApplicationDialog from "@/components/PartnerApplicationDialog";

const BENEFITS = [
  { Icon: Briefcase, title: "Access Live Job Openings", desc: "Get assigned to active mandates from our corporate clients across India." },
  { Icon: Wallet, title: "Attractive Commission", desc: "Transparent, role-specific payouts for every successful placement." },
  { Icon: LineChart, title: "Track Candidate Status", desc: "Real-time visibility across shortlist, interview and selection stages." },
  { Icon: BadgeCheck, title: "Transparent Commission Payments", desc: "Know exactly what's pending, approved and paid — no follow-ups." },
  { Icon: Globe2, title: "Work From Anywhere", desc: "100% remote workflow — submit candidates from any device." },
];

export default function PartnerProgram() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <section
        data-testid="partner-program-section"
        id="partner-program"
        className="py-20 lg:py-24 bg-gradient-to-b from-white via-blue-50/40 to-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading */}
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-[1.1] tracking-tight">
              <span className="inline-block mr-2 align-middle">🤝</span>
              Recruitment Partner{" "}
              <span className="bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent">
                Program
              </span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Join Advance HR Technologies&apos; growing Recruitment Partner Network and earn attractive
              commissions by referring qualified candidates for active job opportunities across India.
            </p>
          </div>

          {/* Sub-heading for benefits */}
          <div className="mt-14 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Why Join Our Network?</p>
          </div>

          {/* 5 Benefit cards */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {BENEFITS.map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white text-primary transition-colors">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-heading font-bold text-slate-900 text-sm leading-snug">{title}</h3>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Two professional CTA buttons */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/login"
              data-testid="partner-program-login-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-primary text-white font-semibold text-sm shadow-sm hover:bg-primary-hover transition-colors min-w-[240px]"
            >
              <Handshake className="w-4 h-4" strokeWidth={2} />
              Recruitment Partner Login
            </Link>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              data-testid="partner-program-join-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-white text-slate-900 font-semibold text-sm border border-slate-200 hover:border-primary hover:text-primary transition-colors min-w-[240px]"
            >
              Join Our Network
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <PartnerApplicationDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
